import {
  AfterViewInit,
  Component,
  OnInit,
  computed,
  effect,
  input,
  output,
  viewChild,
} from '@angular/core';
import { AgGridComponent, FieldTypes } from '@digiwall/ag-grid';
import { ColDef } from '@ag-grid-community/core';

import { LocalizationService, LocalizationModule } from '@abp/ng.core';
import { EligibleCustomerOrderForPreparationDto } from '@digiwall/goliath-documents/proxy';

@Component({
  selector: 'document-eligible-customer-orders-grid',
  templateUrl: './eligible-customer-orders-grid.component.html',
  styleUrl: './eligible-customer-orders-grid.component.scss',
  imports: [AgGridComponent, LocalizationModule],
  standalone: true,
})
export class EligibleCustomerOrdersGridComponent implements OnInit, AfterViewInit {
  /** `null` while the caller is still fetching: neither the grid nor the empty state is shown yet. */
  readonly orders = input<EligibleCustomerOrderForPreparationDto[] | null>(null);
  readonly preSelectedOrderId = input<string | undefined>();
  readonly selectionChange = output<EligibleCustomerOrderForPreparationDto[]>();

  protected readonly hasOrders = computed(() => (this.orders()?.length ?? 0) > 0);
  protected readonly hasNoOrders = computed(() => this.orders()?.length === 0);

  readonly agGrid = viewChild<AgGridComponent>('agGrid');

  protected columnDefs: ColDef[] = [];
  protected selectedRows: EligibleCustomerOrderForPreparationDto[] = [];

  constructor(private localizationService: LocalizationService) {
    this.setupColumnDefinitions();

    effect(() => {
      const orders = this.orders();
      const preSelectedId = this.preSelectedOrderId();
      if (orders?.length && preSelectedId) {
        setTimeout(() => this.selectOrderById(preSelectedId));
      }
    });
  }

  // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method
  ngOnInit() {}

  private selectOrderById(id: string) {
    this.agGrid()?.grid?.api?.forEachNode(node => {
      if (node.data?.id === id) {
        node.setSelected(true);
        this.agGrid()!.grid.api.ensureNodeVisible(node, 'middle');
      }
    });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.onSelectionChanged();
    });
  }

  onSelectionChanged() {
    this.selectionChange.emit(this.selectedRows);
  }

  onSelectedRowsChange() {
    this.selectionChange.emit(this.selectedRows);
  }

  private setupColumnDefinitions() {
    this.columnDefs = [
      {
        headerName: this.localizationService.instant('Doc::OrderDisplayNumber'),
        field: 'displayNumber',
        width: 150,
        pinned: 'left',
        filter: false,
        sort: 'desc',
      },
      {
        headerName: this.localizationService.instant('Doc::OrderDate'),
        field: 'date',
        width: 120,
        filter: false,
        valueFormatter: params => {
          if (params.value) {
            return new Date(params.value).toLocaleDateString();
          }
          return '';
        },
      },
      {
        headerName: this.localizationService.instant('Doc::CustomerReference'),
        field: 'reference',
        width: 150,
        filter: false,
      },
      {
        headerName: this.localizationService.instant('Contact::ThirdParty:Name'),
        field: 'thirdParty',
        width: 200,
        flex: 1,
        filter: false,
      },
      {
        headerName: this.localizationService.instant('Doc::CustomerAddress'),
        field: 'deliveryAddress',
        width: 250,
        flex: 1,
        filter: false,
      },
      {
        headerName: this.localizationService.instant('Doc::TotalAmount'),
        field: 'totalAmountExclTax',
        width: 150,
        type: FieldTypes.Currency,
        filter: false,
      },
      {
        headerName: this.localizationService.instant('Doc::RemainingAmount'),
        field: 'remainingAmountToPrepareExclTax',
        width: 180,
        type: FieldTypes.Currency,
        filter: false,
        cellStyle: params => {
          if (params.value > 0) {
            return { color: '#d73527', fontWeight: 'bold' };
          }
          return null;
        },
      },
    ];
  }

  onGridReady() {
    this.agGrid()!.hidePanelColumn = true;
  }

  selectAll() {
    this.selectedRows = [...(this.orders() ?? [])];
    this.selectionChange.emit(this.selectedRows);
  }

  deselectAll() {
    this.selectedRows = [];
    this.selectionChange.emit(this.selectedRows);
  }

  getSelectedCount(): number {
    return this.selectedRows?.length || 0;
  }
}
